import React from 'react';
import * as Yup from 'yup';
import { CollaborativeChartsReviewItem, CollaborativeChartsReviewItemClinicalFlagReason, collaborativeChartsReviewItemClinicalFlagReasonLabels } from '@uplift/api';
import { Select, SubmitButton, TextInput, useConfirmation } from '@uplift/ui-v2';
import { Box, IconButton, ListItemText, Paper } from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { useCollaborativeChartsReviewClinicalFlagsQuery, useDeleteCollaborativeChartsReviewItemClinicalFlagMutation, useUpdateCollaborativeChartsReviewItemClinicalFlagMutation } from '@uplift/queries';
import { Formik } from 'formik';
import { Close } from '@mui/icons-material';


export const ChartsReviewFlagForm: React.VFC<{ chartsReviewItem: CollaborativeChartsReviewItem }> = ({ chartsReviewItem }) => {
  const { data: clinicalReviewClinicalFlagByResource } = useCollaborativeChartsReviewClinicalFlagsQuery(chartsReviewItem.reviewId);
  const clinicalReviewFlag = clinicalReviewClinicalFlagByResource?.[chartsReviewItem.resourceId];
  const updateClinicalFlagMutation = useUpdateCollaborativeChartsReviewItemClinicalFlagMutation(chartsReviewItem.reviewId, chartsReviewItem.resourceId);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        reviewId: chartsReviewItem.reviewId,
        resourceId: chartsReviewItem.resourceId,
        reason: clinicalReviewFlag?.reason ?? null,
        details: clinicalReviewFlag?.details ?? null,
      }}
      validationSchema={Yup.object().shape({
        reason: Yup.string().required(),
        details: Yup.string().min(1).required(),
      })}
      onSubmit={async (values) => {
        await updateClinicalFlagMutation.mutateAsync(values);
      }}
    >
      {formik => (
        <>
          <Select
            label="Reason"
            name="reason"
            items={[
              CollaborativeChartsReviewItemClinicalFlagReason.clientSafetyConcern,
              CollaborativeChartsReviewItemClinicalFlagReason.other
            ].map(i => ({
              value: i,
              label: collaborativeChartsReviewItemClinicalFlagReasonLabels[i]
            }))}
          />
          <TextInput
            label="Details"
            name="details"
            multiline
            minRows={3}
          />

          {formik.dirty && (
            <Box>
              <SubmitButton>Save</SubmitButton>
            </Box>
          )}
        </>
      )}
    </Formik>
  );
};


export const ChartsReviewFlagDetails: React.VFC<{
  chartsReviewItem: CollaborativeChartsReviewItem;
  setFlagged: (flagged: boolean) => void;
}> = ({ chartsReviewItem, setFlagged }) => {
  const { data: clinicalReviewClinicalFlagByResource } = useCollaborativeChartsReviewClinicalFlagsQuery(chartsReviewItem.reviewId);
  const clinicalReviewFlag = clinicalReviewClinicalFlagByResource?.[chartsReviewItem.resourceId];
  const deleteClinicalFlagMutation = useDeleteCollaborativeChartsReviewItemClinicalFlagMutation(chartsReviewItem.reviewId, chartsReviewItem.resourceId);
  const confirmation = useConfirmation();

  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex' }}>
      <ListItemText
        primary={collaborativeChartsReviewItemClinicalFlagReasonLabels[clinicalReviewFlag.reason]}
        secondary={clinicalReviewFlag.details}
      />
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          size="small"
          onClick={() => {
            confirmation.confirm({
              title: 'Remove flag',
              description: 'Are you sure you want to remove flag?',
              severity: 'error',
              onConfirm: async () => {
                await deleteClinicalFlagMutation.mutateAsync();
                setFlagged(false);
              },
            });
          }}
          loading={deleteClinicalFlagMutation.isPending}
        >
          <Close />
        </IconButton>
      </Box>
    </Paper>
  );
};


export const ChartsReviewFlagSection: React.VFC<{
  chartsReviewItem: CollaborativeChartsReviewItem;
  setFlagged: (flagged: boolean) => void;
}> = ({
  chartsReviewItem,
  setFlagged,
}) => {
  const { data: clinicalReviewClinicalFlagByResource } = useCollaborativeChartsReviewClinicalFlagsQuery(chartsReviewItem.reviewId);
  const clinicalReviewFlag = clinicalReviewClinicalFlagByResource?.[chartsReviewItem.resourceId];

  if (clinicalReviewFlag) {
    return (
      <ChartsReviewFlagDetails chartsReviewItem={chartsReviewItem} setFlagged={setFlagged} />
    );
  }

  return (
    <ChartsReviewFlagForm chartsReviewItem={chartsReviewItem} />
  );
};


export const ChartsReviewFlagButton: React.VFC<{
  flagged: boolean;
  setFlagged: (_: boolean) => void;
}> = ({
  flagged,
  setFlagged
}) => {

  if (flagged) return (
    <FlagIcon
      color="warning"
      sx={{ ml: 2 }}
    />
  );

  return (
    <FlagOutlinedIcon
      sx={{ ml: 2, cursor: 'pointer' }}
      onClick={() => {
        setFlagged(true);
      }}
    />
  );
};
